// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Applications list: search (URL-synced), health from 24-hour counts, and the create dialog.
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Boxes, Plus, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useApplicationsList } from '@/api/queries/applications'
import type { ApplicationListItem } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateApplicationDialog } from '@/features/ops/CreateApplicationDialog'
import { formatNumber } from '@/lib/format'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

export const Route = createFileRoute('/applications/')({
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
  }),
  component: ApplicationsPage,
})

const col = tableColumns<ApplicationListItem>()
const columns = col.columns([
  col.accessor('name', {
    header: 'Application',
    cell: (c) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-ink">{c.getValue()}</div>
        <div className="truncate font-mono text-xs text-ink-3">{c.row.original.slug}</div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor('status', {
    header: 'Status',
    cell: (c) =>
      c.getValue() === 'active' ? (
        <Badge tone="success">Active</Badge>
      ) : (
        <Badge tone="warning">Suspended</Badge>
      ),
    meta: { width: '8rem' },
  }),
  col.accessor('owner_team', {
    header: 'Owner',
    cell: (c) => (
      <div className="min-w-0">
        <div className="truncate">{c.getValue() || <span className="text-ink-3">—</span>}</div>
        {c.row.original.owner_email && (
          <div className="truncate text-xs text-ink-3">{c.row.original.owner_email}</div>
        )}
      </div>
    ),
  }),
  col.accessor('messages_24h', {
    header: 'Last 24 hours',
    cell: (c) => {
      const problems = c.row.original.problems_24h
      return (
        <div>
          <div className="tabular">{formatNumber(c.getValue())} sent</div>
          <div className={problems > 0 ? 'text-xs text-danger' : 'text-xs text-ink-3'}>
            {problems === 0
              ? 'No problems'
              : `${formatNumber(problems)} ${problems === 1 ? 'problem' : 'problems'}`}
          </div>
        </div>
      )
    },
    meta: { align: 'right', width: '9rem', label: 'Last 24 hours' },
  }),
  col.accessor((a) => a.settings?.retention_days ?? 30, {
    id: 'retention',
    header: 'Retention',
    cell: (c) => `${c.getValue()} days`,
    meta: { align: 'right', width: '7rem' },
  }),
  col.accessor('created_at', {
    header: 'Created',
    cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
    sortFn: 'datetime',
    meta: { align: 'right', width: '8rem' },
  }),
  col.accessor('updated_at', {
    header: 'Updated',
    cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
    sortFn: 'datetime',
    meta: { align: 'right', width: '8rem' },
  }),
])

function ApplicationsPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { q } = Route.useSearch()
  const [text, setText] = useState(q ?? '')
  const query = useDebouncedValue(text.trim(), 250)
  const [creating, setCreating] = useState(false)
  const { can } = usePermissions()
  const list = useApplicationsList({ q: query || undefined })

  // Mirror the search box into the URL so the filtered list is shareable.
  useEffect(() => {
    if ((q ?? '') !== query)
      void navigate({ search: (prev) => ({ ...prev, q: query || undefined }), replace: true })
  }, [q, query, navigate])

  return (
    <>
      <PageHeader
        title="Applications"
        description="Systems registered to send email. Each owns its keys, senders, templates, and event types."
        actions={
          <Can permission="manage_config" mode="disable">
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              New application
            </Button>
          </Can>
        }
      />
      <DataTable
        aria-label="Applications"
        columns={columns}
        data={list.items}
        getRowId={(a) => a.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={list.pagination}
        persistKey="applications"
        initialColumnVisibility={{ updated_at: false }}
        initialSorting={[{ id: 'name', desc: false }]}
        onRowClick={(a) => void navigate({ to: '/applications/$appId', params: { appId: a.id } })}
        toolbar={
          <Input
            className="w-72"
            size="sm"
            placeholder="Search by name or slug"
            aria-label="Search applications"
            value={text}
            onChange={(e) => setText(e.target.value)}
            leading={<Search />}
            trailing={
              text ? (
                <IconButton
                  label="Clear search"
                  size="xs"
                  tooltip={false}
                  onClick={() => setText('')}
                >
                  <X />
                </IconButton>
              ) : undefined
            }
          />
        }
        empty={
          query ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching applications"
              description={`Nothing matches “${query}”.`}
            />
          ) : (
            <EmptyState
              icon={Boxes}
              title="No applications yet"
              description="Register the first system that will send email through MailSmith."
              action={
                can('manage_config') ? (
                  <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                    New application
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
      <CreateApplicationDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}
